import { signal } from '@preact/signals';

export type VaultState = 'locked' | 'unlocked' | 'uninitialized';
export const vaultState = signal<VaultState>('uninitialized');

let _pinKey: CryptoKey | null = null;
let _deviceKey: CryptoKey | null = null;

const SK_DB_NAME = '_sk';
const SK_DB_VERSION = 1;
const SK_STORE = 'keys';
const DEVICE_KEY_ID = 'device-key';

function openSkDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(SK_DB_NAME, SK_DB_VERSION);
    req.onupgradeneeded = (): void => {
      const db = req.result;
      if (!db.objectStoreNames.contains(SK_STORE)) {
        db.createObjectStore(SK_STORE);
      }
    };
    req.onsuccess = (): void => resolve(req.result);
    req.onerror = (): void => reject(req.error);
  });
}

async function getStoredDeviceKey(): Promise<CryptoKey | null> {
  const db = await openSkDb();
  return new Promise((resolve) => {
    const tx = db.transaction(SK_STORE, 'readonly');
    const req = tx.objectStore(SK_STORE).get(DEVICE_KEY_ID);
    req.onsuccess = (): void => resolve((req.result as CryptoKey | undefined) ?? null);
    req.onerror = (): void => resolve(null);
  });
}

async function storeDeviceKey(key: CryptoKey): Promise<void> {
  const db = await openSkDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SK_STORE, 'readwrite');
    tx.objectStore(SK_STORE).put(key, DEVICE_KEY_ID);
    tx.oncomplete = (): void => resolve();
    tx.onerror = (): void => reject(tx.error);
  });
}

async function generateDeviceKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export function isPinConfigured(): boolean {
  return localStorage.getItem('_vault_v1') !== null;
}

export async function initVault(): Promise<void> {
  try {
    let key = await getStoredDeviceKey();
    if (!key) {
      key = await generateDeviceKey();
      await storeDeviceKey(key);
    }
    _deviceKey = key;
  } catch {
    _deviceKey = null;
  }

  if (!isPinConfigured()) {
    vaultState.value = 'unlocked';
  } else {
    vaultState.value = 'locked';
  }
}

async function deriveKey(pin: string, salt: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const rawKey = await crypto.subtle.importKey(
    'raw',
    enc.encode(pin),
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100_000,
      hash: 'SHA-256',
    },
    rawKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function setupPin(pin: string): Promise<void> {
  const salt = crypto.getRandomValues(new Uint8Array(32));
  const key = await deriveKey(pin, salt);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const enc = new TextEncoder();
  const verif = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    enc.encode('verified'),
  );
  const payload = JSON.stringify({
    salt: Array.from(salt),
    iv: Array.from(iv),
    verif: Array.from(new Uint8Array(verif)),
  });
  localStorage.setItem('_vault_v1', payload);
  _pinKey = key;
  vaultState.value = 'unlocked';
}

export async function unlockWithPin(pin: string): Promise<boolean> {
  try {
    const raw = localStorage.getItem('_vault_v1');
    if (!raw) return false;
    const { salt, iv, verif } = JSON.parse(raw) as {
      salt: number[];
      iv: number[];
      verif: number[];
    };
    const key = await deriveKey(pin, new Uint8Array(salt));
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: new Uint8Array(iv) },
      key,
      new Uint8Array(verif),
    );
    const text = new TextDecoder().decode(decrypted);
    if (text !== 'verified') return false;
    _pinKey = key;
    vaultState.value = 'unlocked';
    return true;
  } catch {
    return false;
  }
}

export function lock(): void {
  _pinKey = null;
  vaultState.value = 'locked';
}

export function getActivePinKey(): CryptoKey | null {
  return _pinKey;
}

export function getDeviceKey(): CryptoKey | null {
  return _deviceKey;
}
